import { useEffect, useMemo, useState } from 'react'

import { PointArray } from '@editor/interfaces/attribute'
import { LineStyle, PolygonPlaneMeshProps } from '@editor/interfaces/canvas'
import { LINE_WIDTH_TO_GAP_RATIO } from '@editor/pages/projects/blueprint-editor/constants'
import { DEFAULT_STYLES } from '@editor/pages/projects/blueprint-editor/definitions'
import { findCenter } from '@editor/services/Points'
import { RootState } from '@editor/store/app'
import { useFrame, useThree } from '@react-three/fiber'
import { clamp } from 'lodash'
import { useSelector } from 'react-redux'
import { PerspectiveCamera, Vector3 } from 'three'
import { Line2, LineGeometry, LineMaterial } from 'three-stdlib'

import { DEFAULT_LINE_MATERIAL } from '../../Polyline/constants'
import useConfigs from '../hooks/useConfigs'

interface OutlineProps extends PolygonPlaneMeshProps {
  positions: PointArray[]
}

const Outline = (props: OutlineProps) => {
  const { id, positions, isWorking, isEditing, renderOrder } = props

  // Hooks
  const { camera } = useThree()
  const { guideLineConfig } = useConfigs(props)

  // Store
  const currentStrokeColor = useSelector((state: RootState) => state.toolUtil.core.currentStrokeColor)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)

  // Center point of the polygon for line width scaling
  const center = useMemo(() => findCenter(positions.map((p) => new Vector3(...p))), [positions])

  const [outline] = useState(() => {
    if (!guideLineConfig) return null

    const geometry = new LineGeometry()

    geometry.setPositions([...positions, positions[0]].flatMap((p) => p))

    const outlineMat = DEFAULT_LINE_MATERIAL.clone()

    outlineMat.dashed = guideLineConfig?.style === LineStyle.Dashed
    outlineMat.color.set(guideLineConfig?.color || DEFAULT_STYLES.strokeColor || 'black') // Use white when set as transparent since 'transparent' is not a valid color
    outlineMat.linewidth = guideLineConfig?.thickness || 0
    outlineMat.dashSize = outlineMat.linewidth * LINE_WIDTH_TO_GAP_RATIO
    outlineMat.gapSize = outlineMat.linewidth * LINE_WIDTH_TO_GAP_RATIO

    const outlineLn = new Line2(geometry, outlineMat)
    if (renderOrder) outlineLn.renderOrder = renderOrder
    outlineLn.computeLineDistances()

    return outlineLn
  })

  /**
   * Update mesh and outline on points change
   */
  useEffect(() => {
    if (!outline || positions.length < 2) return

    const pts = [...positions, positions[0]]
    const geometry = new LineGeometry()
    geometry.setPositions(pts.map((p) => p).flat())

    outline.geometry.dispose()
    outline.geometry = geometry
    outline.computeLineDistances()
  }, [positions, outline, isWorking, isEditing])

  /**
   * Update the material color when the current stroke color changes.
   * This is used for real-time updates of the stroke color while the user is selecting it to
   * avoid unnecessary re-renders.
   *
   * The color set here _should not_ be the final color. It still must come from the line config prop.
   */
  useEffect(() => {
    if (
      !outline ||
      !(outline.material instanceof LineMaterial) ||
      !guideLineConfig ||
      (!selectedElementIds.includes(id) && !isWorking)
    )
      return

    if (currentStrokeColor) outline.material.color.set(currentStrokeColor)
    else outline.material.color.set(guideLineConfig?.color || DEFAULT_STYLES.strokeColor || 'black')
  }, [outline, guideLineConfig, currentStrokeColor, selectedElementIds, isWorking, id])
  useEffect(() => {
    if (!outline || !(outline.material instanceof LineMaterial) || !guideLineConfig) return

    outline.material.linewidth = guideLineConfig.thickness || 0
    outline.material.dashed = guideLineConfig.style === LineStyle.Dashed
    outline.material.color.set(guideLineConfig.color || DEFAULT_STYLES.strokeColor || 'black')
    outline.material.dashSize = outline.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
    outline.material.gapSize = outline.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
  }, [outline, guideLineConfig, selectedElementIds, id])

  /**
   *  To keep the line thickness constant when zooming in/out.
   */
  useFrame(() => {
    if (camera instanceof PerspectiveCamera && center) {
      // distance from the camera to the anchor point
      const cameraToAnchor = center.clone().sub(camera.position.clone())
      // angle between the camera's forward vector and the vector from the camera to the anchor point
      const angle = cameraToAnchor.angleTo(new Vector3(0, 0, -1).applyQuaternion(camera.quaternion.clone()))
      // center position of viewing plane calculated from the camera's position and the distance to the anchor point
      const centerOfTheAnchorViewingPlane = camera.position
        .clone()
        .sub(new Vector3(0, 0, cameraToAnchor.length() * Math.cos(angle)).applyQuaternion(camera.quaternion.clone()))
      const scaleFactor = clamp(camera.position.distanceTo(centerOfTheAnchorViewingPlane) * 6, 1, 10) / 10

      // gradually increase line width by scaleFactor, thinner line at low scaleFactor, thicker line at high scaleFactor
      if (outline && guideLineConfig) {
        const lineWidth = (guideLineConfig.thickness || 0.001) + scaleFactor ** 2 * 0.005
        outline.material.linewidth = lineWidth
        outline.material.dashSize = outline.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
        outline.material.gapSize = outline.material.linewidth * LINE_WIDTH_TO_GAP_RATIO
      }
    }
  })

  return (
    <group>
      {/* Guide lines between placed points */}
      {outline && (
        <primitive
          object={outline}
          renderOrder={guideLineConfig?.renderOrder}
          visible={guideLineConfig?.visible ?? true}
        />
      )}

      {/* Guide lines's outline */}
    </group>
  )
}

export default Outline
